"use client";

import type { FormVariant } from "@homeinstead/analytics";
import type { Rating as OfficeRating } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type React from "react";
import type {
  CareSeekerLeadFormResponse,
  CareSeekerShallowLeadFormDataV2,
  LeadSource,
} from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { CareOptionType } from "../shared/form-options";
import type { HeroVariant } from "../shared/hero-variants";

import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useActionState, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { submitCareSeekerShallowForm } from "~www/actions/submitCareSeekerShallowForm";
import { useCareSeekerSubmissionResult } from "~www/hooks/useCareSeekerSubmissionResult";
import { useCareSeekerSubmissionTracking } from "~www/hooks/useCareSeekerSubmissionTracking";
import { useHandleFormSubmit } from "~www/hooks/useHandleFormSubmit";
import { useLeadFormTrackingHandlers } from "~www/hooks/useLeadFormTrackingHandlers";
import { careSeekerShallowLeadFormSchemaV2 } from "../care-seeker-shallow-lead-form-schema-v2";
import { getCareSeekerDefaultValues } from "../get-care-seeker-default-values";
import CareOptionStep from "../shared/CareOptionStep";
import { ContactStep } from "../shared/ContactStep";
import { CareOptionsWithBriefDescriptions } from "../shared/form-options";
import { getHeroVariantStyles } from "../shared/hero-variants";
import ThankYou from "../shared/ThankYou";
import CareRecipientStepV2 from "./CareRecipientStepV2";
import UrgencyStep from "./UrgencyStep";

const ERROR_CONTEXT = "CareSeekerLeadForm2";
interface CareSeekerLocalLeadFormV2Props
  extends React.FormHTMLAttributes<HTMLFormElement> {
  currentStep: number;
  handleBack: () => void;
  handleNext: () => void;
  finalStep: number;
  isDebug?: boolean;

  leadSource: LeadSource;

  // From sanity
  heroHeading: PortableTextBlock[];
  placeholders?: Record<string, string>;
  franchiseId: number;
  location: {
    name: string;
    postalCode: string;
  };
  officeRating?: OfficeRating | null;
  phoneNumber: string;
  variant?: HeroVariant;
}

const CareSeekerLocalLeadFormV2 = ({
  className,
  currentStep,
  handleBack,
  handleNext,
  finalStep,
  isDebug,
  heroHeading,
  placeholders,
  location,
  officeRating,
  franchiseId,
  leadSource,
  phoneNumber,
  variant = "default",
  ...props
}: CareSeekerLocalLeadFormV2Props) => {
  const formVariant: FormVariant = "v2" as const;
  const { textColorClass } = getHeroVariantStyles(variant);

  const methods = useForm<CareSeekerShallowLeadFormDataV2>({
    defaultValues: getCareSeekerDefaultValues({
      franchiseId,
      postalCode: location.postalCode,
    }),
    mode: "onSubmit",
    resolver: zodResolver(careSeekerShallowLeadFormSchemaV2),
    shouldUnregister: false,
  });

  const { getValues } = methods;

  const [state, submitFormAction, isPending] = useActionState(
    submitCareSeekerShallowForm,
    null,
  ) as [
    LeadResponse<CareSeekerLeadFormResponse> | null,
    typeof submitFormAction,
    boolean,
  ];

  const handleFormSubmit =
    useHandleFormSubmit<CareSeekerShallowLeadFormDataV2>(methods);

  const [
    careFor,
    careType,
    organizationNumber,
    firstName,
    lastName,
    email,
    phone,
  ] = getValues([
    "careFor",
    "careType",
    "organizationNumber",
    "firstName",
    "lastName",
    "email",
    "phone",
  ]);

  const { memoryCare, everydayTasks, mobilityAssistance, other } = careType;
  const careOptions: CareOptionType[] = useMemo(
    () =>
      [
        memoryCare && "memoryCare",
        everydayTasks && "everydayTasks",
        mobilityAssistance && "mobilityAssistance",
        other && "other",
      ].filter(Boolean) as CareOptionType[],
    [memoryCare, everydayTasks, mobilityAssistance, other],
  );

  const { trackSuccess, trackError } = useLeadFormTrackingHandlers({
    careFor,
    careOptions,
    contactInfo: {
      email,
      firstName,
      lastName,
      phone,
    },
    formVariant,
    isDebug,
    organizationNumber,
  });

  const submissionOptions = {
    errorContext: ERROR_CONTEXT,
    firstName,
    getFirstName: () => getValues("firstName"),
    organizationNumber,
    trackError,
    trackSuccess,
  };
  useCareSeekerSubmissionTracking(state, submissionOptions);
  const submissionResult = useCareSeekerSubmissionResult(
    state,
    submissionOptions,
  );

  if (submissionResult) {
    return (
      <ThankYou firstName={submissionResult.firstName} variant={variant} />
    );
  }

  /**
   * Otherwise render form
   * Note: At the moment, some Steps are rendered in a Suspense boundary because each Step calls useSearchParams
   * via useSetQueryParam hook. This is a Next.js requirement.
   */

  /**
   * CSS Grid "Tallest Child" approach: All steps occupy the same grid cell.
   * The grid height is determined by the tallest child (all participate in layout).
   * Hidden steps use visibility:hidden (invisible) + opacity-0 so they:
   * - Still contribute to grid sizing
   * - Are completely invisible and non-interactive
   * - Have smooth opacity transitions
   */
  const getStepClass = (step: number) =>
    cn(
      "w-full transition-opacity duration-300 [grid-area:1/1]",
      currentStep === step
        ? "z-10 opacity-100"
        : "pointer-events-none z-0 hidden opacity-0 md:invisible md:flex",
    );

  return (
    <FormProvider {...methods}>
      <form
        action={submitFormAction}
        className={cn(
          className,
          "care-seeker-lead-form-v2",
          "flex flex-col items-start pt-8 md:items-center xl:items-start",
        )}
        onSubmit={handleFormSubmit}
        {...props}
      >
        {currentStep > 2 && (
          <Stepper
            className="mx-auto w-full md:w-[440px] xl:ml-0"
            currentStep={currentStep - 2}
            handleBack={handleBack}
            showBackButton={currentStep > 2}
            textColorClass={textColorClass}
            totalSteps={finalStep - 3}
          />
        )}
        <input name="leadSource" type="hidden" value={leadSource} />
        <input name="formVariant" type="hidden" value={formVariant} />

        <input name="organizationNumber" type="hidden" value={franchiseId} />
        <input name="postalCode" type="hidden" value={location.postalCode} />
        <input name="location" type="hidden" value={location.postalCode} />

        <div className="grid w-full overflow-hidden">
          {/**
           * Step 2 - who needs care?
           */}
          <CareRecipientStepV2
            className={getStepClass(2)}
            formVariant={formVariant}
            //   isDebug={isDebug}
            heroHeading={heroHeading}
            iconVariant="outlined"
            nextStep={handleNext}
            officeRating={officeRating}
            phoneNumber={phoneNumber}
            placeholders={placeholders}
            variant={variant}
          />
          {/**
           * Step 3 - kinds of assistance needed
           */}
          <CareOptionStep
            className={getStepClass(3)}
            ctaVariant="primary"
            customOptions={CareOptionsWithBriefDescriptions}
            formVariant={formVariant}
            helperText={
              "If you don't see a specific type of care here, you will be able to provide additional details once we get in touch with you."
            }
            helperTextUnderHeadline={true}
            nextStep={handleNext}
            variant={variant}
          />

          {/**
           * Step 4 - how soon is care needed?
           */}
          <UrgencyStep
            className={getStepClass(4)}
            nextStep={handleNext}
            variant={variant}
          />

          {/**
           * Step 5 - Contact information
           */}
          <ContactStep
            className={getStepClass(5)}
            isPending={isPending}
            variant={variant}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default CareSeekerLocalLeadFormV2;
